import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import { getContentLanguage } from "../../services/LangPrefs";

const CreatorGridService = {
    getGridStats: async () => {
        try {
            const stats = await ApiService.getData(`/cr/grid/stats`);
            return stats;
        } catch (error) {
            Logger.error("Error fetching grid stats:", error);
            throw error;
        }
    },

    getDraftGridPuzzle: async (language = null, type = null) => {
        try {
            const lang = language || getContentLanguage() || 'english';
            const params = { language: lang };
            if (type) {
                params.type = type;
            }
            const draft = await ApiService.getData(`/cr/grid/draft`, params);
            return draft;
        } catch (error) {
            Logger.error("Error fetching draft grid puzzle:", error);
            throw error;
        }
    },

    listGridPuzzles: async (page = 0, pageSize = 10, sort = 'createdAt:desc', keyword = null, state = null, genreId = null, language = null, excludeDiscarded = true, excludeDisabled = true, tags = null) => {
        try {
            const lang = language || getContentLanguage() || 'english';
            const params = {
                page: page,
                pageSize: pageSize,
                sort: sort,
                language: lang
            };
            if (state !== 'DISCARDED' && state !== 'DISABLED') {
                params.excludeDiscarded = excludeDiscarded;
                params.excludeDisabled = excludeDisabled;
            }
            if (keyword) params.keyword = keyword;
            if (state) params.state = state;
            if (genreId) params.genreId = genreId;
            if (tags && Array.isArray(tags) && tags.length > 0) {
                params.tags = tags.map((t) => String(t).trim()).filter(Boolean).join(',');
            }
            const result = await ApiService.getData(`/cr/grid`, params);
            return result;
        } catch (error) {
            Logger.error("Error listing grid puzzles:", error);
            throw error;
        }
    },

    createGridPuzzle: async (title, content, genreId, tags = [], language = null, isKids = false) => {
        try {
            const lang = language || getContentLanguage() || 'english';
            const body = {
                title: title,
                content: content,
                isKids: isKids
            };
            if (genreId) {
                body.genreId = genreId;
            }
            if (tags && Array.isArray(tags) && tags.length > 0) {
                body.tags = tags.map(tag => String(tag).trim()).filter(tag => tag.length > 0);
            }
            const url = `/cr/grid?language=${lang}`;
            const result = await ApiService.postData(url, body);
            return result;
        } catch (error) {
            Logger.error("Error creating grid puzzle:", error);
            throw error;
        }
    },

    /**
     * Apply title hook for a grid puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    applyTitleHook: async (tplId) => {
        try {
            const result = await ApiService.putData(`/cr/grid/${tplId}/title-hook`);
            return result;
        } catch (error) {
            Logger.error(`Error applying title hook for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    updateGridState: async (gridId, state) => {
        try {
            const result = await ApiService.putData(`/cr/grid/${gridId}/state`, { state });
            return result;
        } catch (error) {
            Logger.error(`Error updating grid state for puzzle ${gridId}:`, error);
            throw error;
        }
    },

    getGridTags: async (language = 'english', state = undefined) => {
        try {
            const params = { language };
            if (state != null && state !== '') {
                params.state = state;
            }
            const tags = await ApiService.getData("/cr/grid/tags", params);
            return Array.isArray(tags) ? tags : [];
        } catch (error) {
            Logger.error("Error fetching grid tags:", error);
            throw error;
        }
    }
};

export default CreatorGridService;
